import { Button, Card, CardContent, Label } from "@exhousing/design-system";
import { ClipboardCheck, Plus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { StatusBadge } from "@/components/StatusBadge";
import { useContracts } from "@/hooks/api/useContracts";
import { useCreateInspection, useInspections } from "@/hooks/api/useInspections";

const TYPE_LABELS: Record<string, string> = {
  check_in: "Check-in",
  check_out: "Check-out",
  interim: "Interim",
};

export function InspectionsPage() {
  const { data: inspections } = useInspections();
  const { data: contracts } = useContracts();
  const create = useCreateInspection();
  const [showForm, setShowForm] = useState(false);
  const [contractId, setContractId] = useState("");
  const [type, setType] = useState("check_in");

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!contractId) return;
    create.mutate(
      { contract_id: contractId, type },
      {
        onSuccess: () => {
          toast.success("Inspection created (rooms seeded from the property)");
          setShowForm(false);
          setContractId("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const selectClass =
    "flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Inspections</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Check-in &amp; check-out condition reports
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          New inspection
        </Button>
      </header>

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <form onSubmit={onCreate} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="contract">Contract</Label>
                <select
                  id="contract"
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Select a contract…</option>
                  {(contracts ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.reference ?? `Contract ${c.id.slice(0, 8)}`}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="type">Type</Label>
                <select
                  id="type"
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className={selectClass}
                >
                  <option value="check_in">Check-in</option>
                  <option value="check_out">Check-out</option>
                  <option value="interim">Interim</option>
                </select>
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" disabled={create.isPending || !contractId}>
                  Create
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {inspections && inspections.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">Property</th>
                  <th className="px-6 py-3 font-medium">Type</th>
                  <th className="px-6 py-3 font-medium">Performed</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {inspections.map((i) => (
                  <tr key={i.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3">
                      <Link to={`/inspections/${i.id}`} className="hover:text-primary">
                        <span className="font-medium">
                          {i.property_address ?? "Property"}
                        </span>
                        <span className="block text-xs text-muted-foreground">
                          {i.city ?? ""}
                        </span>
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {TYPE_LABELS[i.type] ?? i.type}
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {i.performed_at ?? "—"}
                    </td>
                    <td className="px-6 py-3">
                      <StatusBadge status={i.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <ClipboardCheck className="size-8 opacity-40" />
              <p className="text-sm">No inspections yet.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
